import axiosInstance from "../axiosInstance";

/* =====================
   TYPES
===================== */

export interface TenantParams {
  tenantId: string;
  page?: number;
  page_size?: number;
  search?: string;
  sort?: string;
  filter?: string;
}

export interface RequestIdParams {
  tenantId: string;
  requestId: string;
}

export interface RequestGuidParams {
  requestGuid: string;
}

export interface CheckUserEligibilityParams {
  tenantId: string;
  phone: string;
}

/* =====================
   GET APIs
===================== */

/** List Organization Users (Paginated) */
export const getOrgUsersApi = async ({
  tenantId,
  page,
  page_size = 20,
  search,
  sort,
  filter,
}: TenantParams) => {
  const params: Record<string, any> = {
    page_size,
    sort,
    filter,
  };
  if (!search && page) {
    params.page = page;
  }

  if (search) {
    params.search = search;
  }

  const response = await axiosInstance.get(
    `/api/users/tenants/${tenantId}/users`,
    { params },
  );

  return response.data;
};

/** Check if user can be added */
export const checkUserEligibilityApi = async ({
  tenantId,
  phone,
}: CheckUserEligibilityParams) => {
  const response = await axiosInstance.get(
    `/api/users/tenants/${tenantId}/add-user/check-eligibility`,
    { params: { phone } },
  );
  return response.data;
};

/** Check Add User Request Status */
export const getAddUserStatusApi = async ({
  tenantId,
  requestId,
}: RequestIdParams) => {
  const response = await axiosInstance.get(
    `/api/users/tenants/${tenantId}/add-user/status/${requestId}`,
  );
  return response.data;
};

/** List Pending Add User Requests (with filter & sort support) */
export const getPendingUserRequestsApi = async ({
  tenantId,
  page,
  page_size = 20,
  sort,
  filter,
}: TenantParams) => {
  const params: Record<string, any> = {
    page_size,
  };

  if (page) {
    params.page = page;
  }

  if (sort) {
    params.sort = sort;
  }

  if (filter) {
    params.filter = filter;
  }

  const response = await axiosInstance.get(
    `/api/users/tenants/${tenantId}/add-user/pending`,
    { params },
  );
  return response.data;
};

/** Get Invite Details (User-side) */
export const getInviteDetailsApi = async ({
  requestGuid,
}: RequestGuidParams) => {
  const response = await axiosInstance.get(
    `/api/users/add-request/${requestGuid}/details`,
  );
  return response.data;
};

/* =====================
   POST APIs
===================== */

/** Initiate Add User */
export interface InitiateAddUserBody {
  target_phone: string;
  target_email?: string;
  target_name?: string;
}

export const initiateAddUserApi = async (
  tenantId: string,
  payload: InitiateAddUserBody,
) => {
  const response = await axiosInstance.post(
    `/api/users/tenants/${tenantId}/add-user/initiate`,
    payload,
  );
  return response.data;
};

/** Verify Admin OTP */
export interface VerifyAdminCodeBody {
  request_id: string;
  verification_code: string;
}

export const verifyAdminCodeApi = async (
  tenantId: string,
  payload: VerifyAdminCodeBody,
) => {
  const response = await axiosInstance.post(
    `/api/users/tenants/${tenantId}/add-user/verify`,
    payload,
  );
  return response.data;
};

/** Update org user status (enable/disable) */
export interface UpdateUserStatusBody {
  status: "active" | "disabled";
  reason?: string;
}

export const updateUserStatusApi = async (
  tenantId: string,
  userId: number,
  payload: UpdateUserStatusBody,
) => {
  const response = await axiosInstance.put(
    `/api/users/tenants/${tenantId}/users/${userId}/status`,
    payload,
  );
  return response.data;
};

/** User Responds to Invite */
export interface RespondInviteBody {
  action: "accept" | "reject";
}

export const respondInviteApi = async (
  requestGuid: string,
  payload: RespondInviteBody,
) => {
  const response = await axiosInstance.post(
    `/api/users/add-request/${requestGuid}/respond`,
    payload,
  );
  return response.data;
};

/* =====================
   RESEND APIs
===================== */

/** Resend Admin OTP */
export const resendAdminOtpApi = async ({
  tenantId,
  requestId,
}: RequestIdParams) => {
  const response = await axiosInstance.post(
    `/api/users/tenants/${tenantId}/add-user/resend/${requestId}`,
  );
  return response.data;
};

/** Resend User Invitation */
export const resendUserInviteApi = async ({
  tenantId,
  requestId,
}: RequestIdParams) => {
  const response = await axiosInstance.post(
    `/api/users/tenants/${tenantId}/add-user/resend-invite/${requestId}`,
  );
  return response.data;
};